import React, { FC, useEffect, useRef, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { IHTMLPopUpTemplateFormData } from '../../../types';

interface IHTMLPopUpPreview {
  htmlPopUpTemplateFormData: IHTMLPopUpTemplateFormData;
}

const HTMLPopUpPreview: FC<IHTMLPopUpPreview> = ({ htmlPopUpTemplateFormData }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const {
    headerText,
    subText,
    merchantLogoSrc,
    merchantLogoAlt,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
    containerWidth,
    containerBackgroundImageSrc,
    containerBackgroundImagePosition,
    containerBackgroundColorOpacity,
    topContainerMargin,
    buttonImageSrc,
    buttonImageAlt,
    merchantLogoWidth,
    merchantLogoMargin,
    textColor,
    textFontFamily,
    textTitleMargin,
    textMargin,
    buttonWidth,
    buttonPadding,
    buttonImageWidth,
    buttonImageMargin,
    buttonSpanCalloutColor,
  } = htmlPopUpTemplateFormData;

  const generateHTML = useCallback(() => {
    const containerBackgroundColorOpacityValue = Number(containerBackgroundColorOpacity) || 75;
    const styles = `
      <style>
      
       body {
        display: flex;
        justify-content: center;
        align-items: center;
       }
  
        
        .button img,
        .button-text {
          vertical-align: middle;
        }

        .container {
          width: ${containerWidth || 0}px;
          padding: 8px;
          border-radius: 16px;
          background-image: url(${containerBackgroundImageSrc || ''});
          background-size: cover;
          background-position: ${containerBackgroundImagePosition?.x || 0}px ${containerBackgroundImagePosition?.y ||
      0}px;
          background-color: rgba(0, 0, 0, ${containerBackgroundColorOpacityValue / 100 || 0.0});
          background-blend-mode: multiply;
        }

        .top {
        margin: ${topContainerMargin?.top || 0}px ${topContainerMargin?.right || 0}px ${topContainerMargin?.bottom ||
      0}px ${topContainerMargin?.left || 0}px;
 
        }

        .top img {
          width: ${merchantLogoWidth || 0}px;
          margin: ${merchantLogoMargin?.top || 0}px ${merchantLogoMargin?.right || 0}px ${merchantLogoMargin?.bottom ||
      0}px ${merchantLogoMargin?.left || 0}px;
        }

        .text {
          color: ${textColor || '#fff'};
          font-family: ${textFontFamily || 'sans-serif'};
          letter-spacing: .5px;
        }

        .text h1 {
          margin: ${textTitleMargin?.top || 0}px ${textTitleMargin?.right || 0}px ${textTitleMargin?.bottom ||
      0}px ${textTitleMargin?.left || 0}px;
          font-size: 16px;
          line-height: 24px;
          font-weight: 500;
        }

        .text p {
          margin: ${textMargin?.top || 0}px ${textMargin?.right || 0}px ${textMargin?.bottom ||
      0}px ${textMargin?.left || 0}px;
          font-size: 12px;
          line-height: 20px;
          font-weight: 400;
        }

        .button button {
          width: ${buttonWidth || 0}%;
          padding: ${buttonPadding?.top || 0}px ${buttonPadding?.right || 0}px ${buttonPadding?.bottom ||
      0}px ${buttonPadding?.left || 0}px;

          border: none;
          border-radius: 16px;
          background-color: #fff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }

        .button img {
          width: ${buttonImageWidth || 0}px;
          margin: ${buttonImageMargin?.top || 0}px ${buttonImageMargin?.right || 0}px ${buttonImageMargin?.bottom ||
      0}px ${buttonImageMargin?.left || 0}px;

        }

        span.callout {
          color: ${buttonSpanCalloutColor || '#fff'};
        }
      </style>
    `;

    const content = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>HTML Pop-up Preview</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Lato:300,400,700" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;700&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@600&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@500&display=swap" />
        ${styles}
      </head>
      <body>
        <div class="container">
          <div class="top">
            <img alt="${merchantLogoAlt || ''}" src="${merchantLogoSrc || ''}" />
          </div>
          <div class="text">
            <h1>${headerText || ''}</h1>
            <p>${subText || ''}</p>
          </div>
          <div class="button">
            <button>
              <img alt="${buttonImageAlt || ''}" src="${buttonImageSrc || ''}" />
              <span class="button-text">
                ${buttonPrefixText || ''} <span class="callout">${buttonTextCallout || ''}</span> ${buttonSuffixText ||
      ''}
              </span>
            </button>
          </div>
        </div>
      </body>
      </html>
    `;

    return content;
  }, [
    containerBackgroundColorOpacity,
    containerWidth,
    containerBackgroundImageSrc,
    containerBackgroundImagePosition,
    topContainerMargin,
    merchantLogoWidth,
    merchantLogoMargin,
    textColor,
    textFontFamily,
    textTitleMargin,
    textMargin,
    buttonWidth,
    buttonPadding,
    buttonImageWidth,
    buttonImageMargin,
    buttonSpanCalloutColor,
    merchantLogoAlt,
    merchantLogoSrc,
    headerText,
    subText,
    buttonImageAlt,
    buttonImageSrc,
    buttonPrefixText,
    buttonTextCallout,
    buttonSuffixText,
  ]);

  const updatePreview = useCallback(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;

      if (doc) {
        const htmlContent = generateHTML();
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(htmlContent, 'text/html');

        // Replace the entire document content
        doc.documentElement.innerHTML = newDoc.documentElement.innerHTML;
      }
    }
  }, [generateHTML]);

  const debouncedUpdatePreview = useMemo(() => debounce(updatePreview, 300), [updatePreview]);

  useEffect(() => {
    debouncedUpdatePreview();

    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedUpdatePreview.cancel();
    };
  }, [debouncedUpdatePreview]);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'lightgrey',
        borderRadius: '16px',
        padding: '20px',
      }}
    >
      <iframe
        ref={iframeRef}
        style={{
          width: '100%',
          height: 'auto',
          border: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          resize: 'vertical',
          overflow: 'auto',
          minHeight: '200px',
          backgroundColor: 'lightgrey',
        }}
        title="HTML Pop-up Preview"
        sandbox="allow-same-origin"
      />
    </div>
  );
};

export default HTMLPopUpPreview;
